import fs from 'fs-extra'
import path from 'path'
import matter from 'gray-matter'
import { v4 as uuidv4 } from 'uuid'
import slugify from 'slugify'

export type DocumentType = 'guide' | 'api' | 'tutorial' | 'reference' | 'spec'
export type DocumentCategory = 'product' | 'tech' | 'system' | 'user'
export type DocumentStatus = 'draft' | 'review' | 'published' | 'archived'

export interface Document {
  id: string
  title: string
  content: string
  type: DocumentType
  category: DocumentCategory
  status: DocumentStatus
  tags: string[]
  author: string
  created: string
  updated: string
  version: string
}

export interface DocumentFilter {
  type?: DocumentType
  category?: DocumentCategory
  status?: DocumentStatus
  tags?: string[]
}

export interface SearchResult {
  document: Document
  score: number
  highlights?: string[]
}

export class DocumentManager {
  private docsDir: string

  constructor(workflowDir: string) {
    this.docsDir = path.join(workflowDir, 'docs')
  }

  async ensureDocsDir(): Promise<void> {
    await fs.ensureDir(this.docsDir)
  }

  async create(data: {
    title: string
    content: string
    type: DocumentType
    category: DocumentCategory
    tags: string[]
    author: string
  }): Promise<Document> {
    await this.ensureDocsDir()

    const id = slugify(data.title, { lower: true, strict: true }) + '-' + uuidv4().slice(0, 8)
    const now = new Date().toISOString()

    const doc: Document = {
      id,
      title: data.title,
      content: data.content,
      type: data.type,
      category: data.category,
      status: 'draft',
      tags: data.tags,
      author: data.author,
      created: now,
      updated: now,
      version: '1.0.0'
    }

    await this.save(doc)
    return doc
  }

  async save(doc: Document): Promise<void> {
    const filePath = path.join(this.docsDir, `${doc.id}.md`)
    
    // Extract content and metadata
    const { content, ...metadata } = doc
    
    // Create markdown with frontmatter
    const fileContent = matter.stringify(content, metadata)
    
    await fs.writeFile(filePath, fileContent, 'utf-8')
  }

  async getById(id: string): Promise<Document | null> {
    const filePath = path.join(this.docsDir, `${id}.md`)
    
    if (!await fs.pathExists(filePath)) {
      return null
    }

    const fileContent = await fs.readFile(filePath, 'utf-8')
    const { data, content } = matter(fileContent)
    
    return {
      ...data,
      content
    } as Document
  }

  async list(filter?: DocumentFilter): Promise<Document[]> {
    await this.ensureDocsDir()
    
    const files = await fs.readdir(this.docsDir)
    const docs: Document[] = []
    
    for (const file of files) {
      if (!file.endsWith('.md')) continue
      
      const id = file.replace('.md', '')
      const doc = await this.getById(id)
      
      if (!doc) continue
      
      // Apply filters
      if (filter) {
        if (filter.type && doc.type !== filter.type) continue
        if (filter.category && doc.category !== filter.category) continue
        if (filter.status && doc.status !== filter.status) continue
        if (filter.tags && filter.tags.length > 0) {
          const hasAllTags = filter.tags.every(tag => doc.tags.includes(tag))
          if (!hasAllTags) continue
        }
      }
      
      docs.push(doc)
    }
    
    // Sort by updated date (newest first)
    return docs.sort((a, b) => 
      new Date(b.updated).getTime() - new Date(a.updated).getTime()
    )
  }

  async update(id: string, updates: Partial<Document>): Promise<Document | null> {
    const doc = await this.getById(id)
    if (!doc) return null
    
    const updatedDoc: Document = {
      ...doc,
      ...updates,
      id: doc.id, // Prevent ID change
      created: doc.created, // Preserve creation date
      updated: new Date().toISOString()
    }
    
    await this.save(updatedDoc)
    return updatedDoc
  }

  async delete(id: string): Promise<boolean> {
    const filePath = path.join(this.docsDir, `${id}.md`)
    
    if (!await fs.pathExists(filePath)) {
      return false
    }
    
    await fs.remove(filePath)
    return true
  }

  async search(query: string): Promise<SearchResult[]> {
    const docs = await this.list()
    const results: SearchResult[] = []
    const queryLower = query.toLowerCase()
    
    for (const doc of docs) {
      let score = 0
      const highlights: string[] = []
      
      // Search in title (higher weight)
      if (doc.title.toLowerCase().includes(queryLower)) {
        score += 10
        highlights.push(`Title: ${doc.title}`)
      }
      
      // Search in content
      const contentLower = doc.content.toLowerCase()
      if (contentLower.includes(queryLower)) {
        score += 5
        
        // Extract context around match
        const index = contentLower.indexOf(queryLower)
        const start = Math.max(0, index - 50)
        const end = Math.min(doc.content.length, index + query.length + 50)
        const excerpt = doc.content.substring(start, end)
        highlights.push(`...${excerpt}...`)
      }
      
      // Search in tags
      const matchingTags = doc.tags.filter(tag => 
        tag.toLowerCase().includes(queryLower)
      )
      if (matchingTags.length > 0) {
        score += 3 * matchingTags.length
        highlights.push(`Tags: ${matchingTags.join(', ')}`)
      }
      
      if (score > 0) {
        results.push({
          document: doc,
          score: score / 20, // Normalize to 0-1
          highlights
        })
      }
    }
    
    // Sort by score (highest first)
    return results.sort((a, b) => b.score - a.score)
  }

  async exportAsMarkdown(id: string): Promise<string> {
    const doc = await this.getById(id)
    if (!doc) {
      throw new Error(`Document ${id} not found`)
    }
    
    // Return full markdown with frontmatter
    const { content, ...metadata } = doc
    return matter.stringify(content, metadata)
  }

  async exportAsHtml(id: string): Promise<string> {
    const doc = await this.getById(id)
    if (!doc) {
      throw new Error(`Document ${id} not found`)
    }
    
    // Simple HTML template
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${doc.title}</title>
  <style>
    body { 
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; 
      max-width: 800px; 
      margin: 0 auto; 
      padding: 40px 20px;
      line-height: 1.6;
    }
    h1, h2, h3 { color: #333; }
    code { 
      background: #f4f4f4; 
      padding: 2px 4px; 
      border-radius: 3px; 
    }
    pre { 
      background: #f4f4f4; 
      padding: 15px; 
      border-radius: 5px; 
      overflow-x: auto; 
    }
    blockquote { 
      border-left: 4px solid #ddd; 
      margin: 0; 
      padding-left: 20px; 
      color: #666; 
    }
    .metadata { 
      background: #f9f9f9; 
      padding: 15px; 
      border-radius: 5px; 
      margin-bottom: 30px; 
    }
    .metadata dt { 
      font-weight: bold; 
      display: inline-block; 
      width: 100px; 
    }
    .metadata dd { 
      display: inline; 
      margin: 0; 
    }
  </style>
</head>
<body>
  <div class="metadata">
    <dl>
      <dt>Type:</dt><dd>${doc.type}</dd><br>
      <dt>Category:</dt><dd>${doc.category}</dd><br>
      <dt>Status:</dt><dd>${doc.status}</dd><br>
      <dt>Author:</dt><dd>${doc.author}</dd><br>
      <dt>Updated:</dt><dd>${new Date(doc.updated).toLocaleString()}</dd><br>
      <dt>Tags:</dt><dd>${doc.tags.join(', ')}</dd>
    </dl>
  </div>
  
  <h1>${doc.title}</h1>
  
  ${this.markdownToHtml(doc.content)}
</body>
</html>`
  }

  private markdownToHtml(markdown: string): string {
    // Very basic markdown to HTML conversion
    return markdown
      // Headers
      .replace(/^### (.+)$/gm, '<h3>$1</h3>')
      .replace(/^## (.+)$/gm, '<h2>$1</h2>')
      .replace(/^# (.+)$/gm, '<h1>$1</h1>')
      // Bold
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      // Italic
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      // Code blocks
      .replace(/```(\w+)?\n([\s\S]+?)```/g, '<pre><code>$2</code></pre>')
      // Inline code
      .replace(/`([^`]+)`/g, '<code>$1</code>')
      // Links
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')
      // Line breaks
      .replace(/\n\n/g, '</p><p>')
      // Wrap in paragraphs
      .replace(/^(.+)$/gm, '<p>$1</p>')
      // Remove empty paragraphs
      .replace(/<p><\/p>/g, '')
  }
}